import type {ReactElement} from 'react';
import React from 'react';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {Text, TextSize} from '@Components/text';
import styles from './download-video-duration-information.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {DOWNLOAD_GIF_DEFAULT_DURATION} from '@Components/download-panel/components/download-gif-duration-option/download-gif-duration-option.types';
import {isPosterVideo} from '@Libraries/poster-object.library';

export function DownloadVideoDurationInformation(): ReactElement | null {
  const {speed, posterObject, fileType, duration, setDuration, setSpeed, originalDuration} = useDownloadPanelData();

  if (!isPosterVideo(posterObject)) {
    return null;
  }

  function getDownloadOptionText(originalDuration: number, duration: number, speed: number): {icon: string; text: string} | null {
    const contentNeeded = duration * speed;
    const epsilon = 0.01;

    if (Math.abs(contentNeeded - originalDuration) < epsilon) {
      if (speed === 1) {
        return null;
      }
      if (speed < 1) {
        return {icon: 'icon-slow-speed', text: window.i18next.t('pmwjs_plays_once_slowly')};
      }
      if (speed > 1) {
        return {icon: 'icon-fast-speed', text: window.i18next.t('pmwjs_plays_once_faster')};
      }
    }

    if (contentNeeded > originalDuration) {
      const loopCount = Math.floor(contentNeeded / originalDuration);
      const remainder = contentNeeded % originalDuration;

      const times = loopCount === 1 ? 'time' : 'times';
      const remainingTime = parseFloat(remainder.toFixed(1));

      if (Math.abs(remainingTime) < epsilon) {
        return {
          icon: 'icon-repeat',
          text: window.i18next.t('pmwjs_loops_x_times', {
            amount: loopCount,
            times: times,
          }),
        };
      }

      const s = remainingTime === 1 ? '' : 's';
      return {
        icon: 'icon-repeat',
        text: window.i18next.t('pmwjs_loops_x_times_with_y_remaining_seconds', {
          amount: loopCount,
          times: times,
          remainingSeconds: remainingTime,
          plural: s,
        }),
      };
    }

    if (contentNeeded < originalDuration) {
      const trimmedTime = parseFloat((originalDuration - contentNeeded).toFixed(1));
      const s = trimmedTime === 1 ? '' : 's';
      return {
        icon: 'icon-cut',
        text: window.i18next.t('pmwjs_trims_x_seconds_of_content', {
          time: trimmedTime,
          plural: s,
        }),
      };
    }

    return null;
  }

  const onReset = (): void => {
    if (fileType === FileTypeOptions.GIF) {
      setDuration(DOWNLOAD_GIF_DEFAULT_DURATION);
    } else {
      setDuration(originalDuration);
    }
    setSpeed(1);
  };

  const getDurationInformation = (): ReactElement | null => {
    const details = getDownloadOptionText(originalDuration, duration, speed);
    if (details) {
      return (
        <div className={`_full-width radius-8 ${styles.container} flex-row-justify-center flex-items-center`}>
          <Icon size={IconSize.SIZE_ICON_16} className="content-primary spacing-m-r-1" icon={details.icon} />
          <Text dangerouslySetInnerHTML className="content-primary spacing-m-r-1" size={TextSize.XXSMALL} bold val={details.text} />
          <Text onClick={onReset} bold className="content-primary spacing-m-r-1 _cursor-pointer" underline size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_reset')} />
        </div>
      );
    }
    return null;
  };

  return getDurationInformation();
}
